import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useSalesStatus } from "../components/PhysicalSalesBanner";
import ProductCard from "../components/ProductCard";
import { tr, pick } from "../i18n/translations";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Seo, { breadcrumbJsonLd, faqJsonLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";

const AGES = ["3-6", "6-9", "9-12", "ado", "adulte"];
const TROUBLES = [
  "Dyslexie",
  "Dysorthographie",
  "Dyscalculie",
  "Dysphasie",
  "Dyspraxie",
  "TDAH",
  "Autisme",
  "Bégaiement",
  "Troubles articulatoires",
  "Retard de parole",
  "Retard de langage",
  "Dysphagies",
  "Troubles vélo-tubo-tympaniques",
  "Aphasies",
];
const PRICE_RANGES = [
  { label: "Moins de 10 €", min: 0, max: 10 },
  { label: "10 – 25 €", min: 10, max: 25 },
  { label: "25 – 50 €", min: 25, max: 50 },
  { label: "50 – 100 €", min: 50, max: 100 },
  { label: "Plus de 100 €", min: 100, max: 9999 },
];

export default function CategoryPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { addToCart, lang } = useApp();
  const { should_block_physical, showBlockingModal } = useSalesStatus();

  function handleAdd(prod) {
    const isPhysical = !prod?.is_digital && !prod?.no_shipping;
    if (should_block_physical && isPhysical) {
      showBlockingModal();
      return;
    }
    addToCart(prod);
    toast.success("Ajouté");
  }
  const [category, setCategory] = useState(null);
  const [subcats, setSubcats] = useState([]);
  const [products, setProducts] = useState([]);
  const [age, setAge] = useState(params.get("age") || "");
  const [troubles, setTroubles] = useState(params.get("trouble") ? [params.get("trouble")] : []); // multi-select
  const [priceIdx, setPriceIdx] = useState(params.get("price") || "");
  const [showAllTroubles, setShowAllTroubles] = useState(false);
  const q = params.get("q") || "";

  useEffect(() => {
    if (slug) {
      api.get(`/categories/${slug}`).then((r) => setCategory(r.data));
      api.get("/categories").then((r) => {
        const all = r.data || [];
        setSubcats(all.filter((c) => c.parent_slug === slug && c.active !== false));
      });
    }
  }, [slug]);

  useEffect(() => {
    // For multi-trouble, we fetch one request per trouble and merge (backend doesn't support multi-value)
    async function fetchProducts() {
      if (troubles.length === 0 || troubles.length === 1) {
        const p = new URLSearchParams();
        if (slug) p.set("category", slug);
        if (age) p.set("age", age);
        if (troubles[0]) p.set("trouble", troubles[0]);
        if (priceIdx !== "" && PRICE_RANGES[Number(priceIdx)]) {
          const r = PRICE_RANGES[Number(priceIdx)];
          p.set("min_price", String(r.min));
          p.set("max_price", String(r.max));
        }
        if (q) p.set("q", q);
        const r = await api.get(`/products?${p.toString()}`);
        setProducts(r.data);
      } else {
        // Multi: fetch in parallel for each trouble, dedupe by slug
        const requests = troubles.map((t) => {
          const p = new URLSearchParams();
          if (slug) p.set("category", slug);
          if (age) p.set("age", age);
          p.set("trouble", t);
          if (priceIdx !== "" && PRICE_RANGES[Number(priceIdx)]) {
            const r = PRICE_RANGES[Number(priceIdx)];
            p.set("min_price", String(r.min));
            p.set("max_price", String(r.max));
          }
          return api.get(`/products?${p.toString()}`);
        });
        const results = await Promise.all(requests);
        const seen = new Set();
        const merged = [];
        for (const r of results) {
          for (const prod of r.data) {
            if (!seen.has(prod.slug)) { seen.add(prod.slug); merged.push(prod); }
          }
        }
        setProducts(merged);
      }
    }
    fetchProducts();
  }, [slug, age, JSON.stringify(troubles), priceIdx, q]);

  function toggleTrouble(t) {
    setTroubles((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);
  }

  const title = slug ? (category ? pick(category.name, lang) : "...") : (q ? `Recherche : « ${q} »` : "Catalogue complet");
  const hasFilters = age || troubles.length > 0 || priceIdx !== "";
  const hasSubs = subcats.length > 0;

  // ─── SEO enrichi : H1 clinique unique par catégorie ────────────────
  const catName = category ? pick(category.name, lang) : "";
  const richH1 = category
    ? `Jeux et matériel d'orthophonie — ${catName}`
    : (q ? `Recherche : « ${q} »` : "Matériel d'orthophonie : catalogue complet");
  const seoTitle = category
    ? `Jeu orthophonie ${catName} · Matériel validé par les cliniciens`
    : (q ? `Recherche « ${q} »` : "Catalogue matériel orthophonie · +40 outils cliniques");
  const seoDesc = category
    ? (pick(category.description, lang) || `${catName} pour orthophonistes : jeux, cartes, cahiers et supports validés cliniquement. Livraison France & UE offerte dès 100 € · Sélection thérapeutes.`)
    : `Catalogue complet de matériel d'orthophonie et ressources pour orthophonistes — ${products.length} produits cliniquement validés, expédition rapide.`;
  const seoKeywords = category
    ? `jeu orthophonie ${catName}, matériel orthophonie ${catName}, ressources pour orthophonistes${troubles.length ? ", " + troubles.join(", ") : ""}`
    : `matériel orthophonie, jeu orthophonie, ressources pour orthophonistes, catalogue orthophoniste${troubles.length ? ", " + troubles.join(", ") : ""}`;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={slug ? `/categorie/${slug}` : "/catalogue"}
        keywords={seoKeywords}
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Catalogue", path: "/catalogue" },
            ...(category ? [{ name: pick(category.name, lang), path: `/categorie/${slug}` }] : []),
          ]),
          // CollectionPage schema for the category
          ...(category ? [{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: seoTitle,
            description: seoDesc,
            url: `https://langageonline.fr/categorie/${slug}`,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: products.length,
              itemListElement: products.slice(0, 12).map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://langageonline.fr/produit/${p.slug}`,
                name: pick(p.name, lang),
              })),
            },
          }] : []),
          // FAQPage schema — for Google featured snippets / PAA
          ...(category && Array.isArray(category.faqs) && category.faqs.length > 0
            ? [faqJsonLd(category.faqs)]
            : []),
        ]}
      />
      <div className="mb-10">
        <Breadcrumbs items={[
          { name: "Accueil", path: "/" },
          { name: "Catalogue", path: "/catalogue" },
          ...(category ? [{ name: pick(category.name, lang) }] : []),
        ]} />
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mt-3" data-testid="cat-title">
          {richH1}
        </h1>
        {category && <p className="mt-3 text-ink-soft max-w-3xl leading-relaxed">{pick(category.description, lang) || `${catName} : découvrez notre sélection d'outils d'orthophonie cliniquement validés pour la rééducation en cabinet, à l'école ou à domicile.`}</p>}
      </div>

      {/* Special manifesto block for the wellness category */}
      {slug === "sante-bien-etre" && (
        <section
          data-testid="sante-manifesto"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sage/30 via-ivory-200 to-coral/15 border border-sage/40 p-8 lg:p-12 mb-12"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-coral/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-64 h-64 bg-sage/30 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-coral-dark mb-3">Pourquoi cette rubrique ?</p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-light leading-tight tracking-tight mb-5">
              Un thérapeute bien dans sa tête et dans son corps <em className="text-coral-dark">est un meilleur thérapeute.</em>
            </h2>
            <div className="space-y-3 text-ink/85 text-base md:text-lg leading-relaxed">
              <p>
                Orthophoniste, enseignant spécialisé, AESH, orthopédagogue : ton métier exige une présence pleine, séance après séance.
                Ton attention, ta voix, ta posture, ta patience — tout cela puise dans une réserve qui se vide.
              </p>
              <p>
                Cette rubrique <strong>n'est pas un accessoire</strong>. Elle est née d'un constat simple :
                on ne prend pas soin des autres durablement sans prendre soin de soi.
                Tu y trouveras des ressources sélectionnées pour <strong>protéger ton dos, calmer ton mental,
                restaurer ta voix, retrouver de l'énergie</strong> — bref, tenir la distance avec joie.
              </p>
              <p className="text-ink-soft italic">
                Parce que ta santé est aussi la santé de ceux que tu accompagnes.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* If subcategories exist, show them first */}
      {hasSubs && (
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-ink-soft mb-4">Sous-catégories</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="subcat-grid">
            {subcats.map((s) => (
              <Link
                key={s.slug}
                to={`/categorie/${s.slug}`}
                className="group flex items-center justify-between gap-4 bg-white border border-ink/10 rounded-2xl p-5 hover:border-pedablue hover:shadow-md transition-all"
                data-testid={`subcat-${s.slug}`}
              >
                <div>
                  <p className="font-display text-lg group-hover:text-pedablue transition">{pick(s.name, lang)}</p>
                  <p className="text-xs text-ink-soft mt-1 line-clamp-1">{pick(s.description, lang)}</p>
                </div>
                <ArrowRight size={16} className="text-ink-soft group-hover:text-pedablue group-hover:translate-x-1 transition" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-ink/10 rounded-3xl p-5 mb-10" data-testid="filters-panel">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <Filter label={tr(lang, "filter_age")} value={age} setValue={setAge} options={AGES.map((a) => ({ value: a, label: a }))} testid="filter-age" />
          <Filter
            label={tr(lang, "filter_price")}
            value={priceIdx}
            setValue={setPriceIdx}
            options={PRICE_RANGES.map((r, i) => ({ value: String(i), label: r.label }))}
            testid="filter-price"
          />
          {hasFilters && (
            <button onClick={() => { setAge(""); setTroubles([]); setPriceIdx(""); }} className="text-sm underline text-ink-soft" data-testid="filter-reset">
              Réinitialiser
            </button>
          )}
        </div>

        {/* Multi-select troubles */}
        <div>
          <p className="text-xs uppercase tracking-widest text-ink-soft mb-2">
            {tr(lang, "filter_trouble")} {troubles.length > 0 && <span className="text-pedablue">({troubles.length} sélectionné{troubles.length > 1 ? "s" : ""})</span>}
          </p>
          <div className="flex flex-wrap gap-2" data-testid="trouble-checkboxes">
            {(showAllTroubles ? TROUBLES : TROUBLES.slice(0, 8)).map((t) => {
              const checked = troubles.includes(t);
              return (
                <label
                  key={t}
                  aria-pressed={checked}
                  className={`px-3 py-1.5 rounded-full text-xs cursor-pointer transition border ${checked ? "bg-pedablue text-ivory border-pedablue" : "bg-ivory-200 border-transparent hover:border-pedablue/40"}`}
                  data-testid={`trouble-${t}`}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleTrouble(t)} className="hidden" />
                  {t}
                </label>
              );
            })}
            {!showAllTroubles && TROUBLES.length > 8 && (
              <button onClick={() => setShowAllTroubles(true)} className="text-xs text-pedablue underline px-2 py-1.5">
                + {TROUBLES.length - 8} autres
              </button>
            )}
          </div>
        </div>
      </div>

      {products.length === 0 ? (
        <p className="text-ink-soft py-20 text-center">{hasSubs ? "Sélectionne une sous-catégorie ci-dessus." : "Aucun produit ne correspond à vos critères."}</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="cat-product-grid">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} onAdd={handleAdd} />
          ))}
        </div>
      )}

      {/* FAQ SEO — Rich snippets Google (featured snippets / People Also Ask) */}
      {category && Array.isArray(category.faqs) && category.faqs.length > 0 && (
        <section className="mt-16" data-testid="category-faq">
          <p className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Vos questions fréquentes</p>
          <h2 className="font-display text-2xl md:text-3xl mb-6">
            {pick(category.name, lang)} — questions les plus posées
          </h2>
          <div className="space-y-2 max-w-4xl">
            {category.faqs.map((f, i) => (
              <details key={i} className="bg-white rounded-2xl border border-ink/10 p-4 group" data-testid={`cat-faq-${i}`}>
                <summary className="cursor-pointer font-medium text-sm md:text-base flex justify-between items-center gap-3">
                  <span>{f.q}</span>
                  <span className="text-pedablue transition-transform group-open:rotate-45 text-xl">+</span>
                </summary>
                <p className="text-sm text-ink-soft mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Filter({ label, value, setValue, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className="bg-white border border-ink/15 rounded-full px-5 py-2.5 text-sm outline-none focus:border-pedablue cursor-pointer"
    >
      <option value="">{label} — Tous</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
